import {
  AlertCircleIcon,
  CheckCircle2Icon,
  CircleIcon,
  CircleStopIcon,
  Clock3Icon,
  KeyRoundIcon,
  LoaderIcon,
  PauseCircleIcon,
  WalletCardsIcon,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { PERSONAL_ACCOUNT_COPY } from '@/features/identity';
import type { CardExecutionState } from './executionStatus';
import type { BoardStateFilter } from './searchParams';

/**
 * One row per status: its glyph, its colour, the words a card shows, and the
 * name assistive technology is given. Every surface that displays a status
 * reads this table and nothing else — it is the only reason the board and the
 * task detail cannot drift apart, and adding a second lookup somewhere would
 * quietly undo that.
 *
 * `cardLabel` of `null` means the status is silent on a board card. Three are:
 * a task never started, one already finished, and one simply at rest all want
 * nothing from anyone, and a board where every card is marked says nothing.
 *
 * Colour classes are spelled out rather than built from a template so
 * Tailwind's build-time scanner can find them, and every one of them resolves
 * through a semantic board-state token rather than a palette value.
 */
export interface StatusPresentation {
  Icon: LucideIcon;
  /** Colour class, or null for a status shown without one. */
  colourClass: string | null;
  /** What a board card shows, or null when the card stays silent. */
  cardLabel: string | null;
  /** The canonical name, used in task detail and by assistive technology. */
  canonicalName: string;
  /** Whether a change to this status announces itself. */
  announces: boolean;
  spin?: boolean;
}

export const STATUS_PRESENTATION: Record<
  CardExecutionState['kind'],
  StatusPresentation | null
> = {
  'not-started': {
    Icon: CircleIcon,
    colourClass: 'text-muted-foreground',
    cardLabel: null,
    canonicalName: 'Not Started',
    announces: false,
  },
  live: {
    Icon: LoaderIcon,
    colourClass: 'text-board-state-running',
    cardLabel: 'In Progress',
    canonicalName: 'In Progress',
    announces: true,
    spin: true,
  },
  failed: {
    Icon: AlertCircleIcon,
    colourClass: 'text-board-state-failed',
    cardLabel: 'Error',
    canonicalName: 'Error',
    announces: false,
  },
  blocked: {
    // A distinct glyph, not the failure triangle. The two states must be
    // distinguishable without reading the colour (spec 072 FR-023), and a reader
    // scanning a board should be able to tell "this broke" from "this was stopped"
    // before either word registers.
    Icon: WalletCardsIcon,
    // The waiting colour, not the failure colour. A blocked run is a task waiting on a
    // person — an administrator, rather than the reader — and colouring it as an error
    // would put it in the same visual bucket as work that actually went wrong, which is
    // precisely the confusion this status exists to prevent.
    colourClass: 'text-board-state-waiting',
    cardLabel: 'Spend limit',
    canonicalName: 'Stopped by spend limit',
    announces: false,
  },
  stopped: {
    // A distinct glyph, for the same reason `blocked` does not reuse the failure
    // triangle: the three terminal readings must be told apart without reading
    // the colour (spec 072 FR-023).
    Icon: CircleStopIcon,
    // Muted, not a state colour. The board spends colour on what wants
    // something from someone — work in flight, work that broke, work waiting on
    // a person. A run someone stopped on purpose wants nothing; it is a fact
    // about what happened, and colouring it would put a deliberate act in the
    // same bucket as an unplanned one.
    colourClass: 'text-muted-foreground',
    // "Stopped" on the card, "Stopped by a person" everywhere else. `blocked`
    // already reads as "Stopped by spend limit", and FR-006 requires a run
    // someone stopped to be tellable from one refused for any other reason —
    // so the canonical name names the agent, matching word for word the message
    // the service writes onto the run itself.
    cardLabel: 'Stopped',
    canonicalName: 'Stopped by a person',
    // Whoever stopped it already knows. Announcing it would narrate the reader's
    // own action back to them.
    announces: false,
  },
  'needs-account': {
    // A key, not the wallet: this run waits on its person linking an account,
    // not on anyone's budget (spec 160). The waiting colour, as `blocked`'s,
    // because it waits on a person.
    Icon: KeyRoundIcon,
    colourClass: 'text-board-state-waiting',
    cardLabel: PERSONAL_ACCOUNT_COPY.cardLabel,
    canonicalName: PERSONAL_ACCOUNT_COPY.canonicalName,
    announces: false,
  },
  queued: {
    // A clock: waiting its turn, which is neither work in flight (no spinner) nor
    // work waiting on the reader. Distinct from every other glyph so the state
    // reads without the colour (spec 072 FR-023).
    Icon: Clock3Icon,
    // Muted, like `stopped`. A queued run wants nothing from anyone here — it
    // starts by itself when a slot frees — and the waiting colour would tell the
    // reader to act (spec 115 FR-002).
    colourClass: 'text-muted-foreground',
    cardLabel: 'Queued',
    canonicalName: 'Queued',
    // Silent, as `waiting-human` is: the place in line changes on every refresh,
    // and announcing each change would be noise.
    announces: false,
  },
  'waiting-human': {
    Icon: PauseCircleIcon,
    colourClass: 'text-board-state-waiting',
    // The one place the card's words and the canonical name differ. "Waiting
    // on you" is what a member scanning a board needs; "Human Input Needed" is
    // what the rest of the product calls it. Someone only reading the
    // workspace is told "Waiting on a member" instead: see
    // `statusPresentationFor` below.
    cardLabel: 'Waiting on you',
    canonicalName: 'Human Input Needed',
    announces: false,
  },
  completed: {
    Icon: CheckCircle2Icon,
    // Its light-theme contrast is 4.58:1, clearing AA by 0.08. Nothing here may
    // reduce its opacity or drop below normal text weight without that being
    // re-verified.
    colourClass: 'text-board-state-done',
    cardLabel: null,
    canonicalName: 'Completed',
    announces: false,
  },
  // At rest, with nothing worth saying about it in either density.
  idle: null,
};

/**
 * What a human gate is called for someone who cannot answer it (spec 193).
 *
 * "Waiting on you" is true of a member and false of a reader: someone reading
 * an Organization workspace they are not in cannot approve, reject or reply,
 * and telling them it is their turn sends them to a control they do not have.
 * The gate waits on the workspace's members, and that is what they are told.
 */
export const READER_WAITING_LABEL = 'Waiting on a member';

const READER_WAITING_HUMAN: StatusPresentation = {
  // `STATUS_PRESENTATION['waiting-human']` with the reader's words; the glyph,
  // colour and canonical name are the same gate.
  Icon: PauseCircleIcon,
  colourClass: 'text-board-state-waiting',
  cardLabel: READER_WAITING_LABEL,
  canonicalName: 'Human Input Needed',
  announces: false,
};

/**
 * The status table's row for `kind`, in the words that are true of the
 * person reading it. `canWork` is `useCanWork()`: false only for a reader of
 * an Organization workspace. The one row that differs is the human gate.
 */
export function statusPresentationFor(
  kind: CardExecutionState['kind'],
  canWork: boolean,
): StatusPresentation | null {
  if (!canWork && kind === 'waiting-human') return READER_WAITING_HUMAN;
  return STATUS_PRESENTATION[kind];
}

/* ---------------------------------------------------------------------------
   Task state, as the board, the task list and Overview summarise it.

   `STATUS_PRESENTATION` above answers "what is this *execution* doing"; this
   answers the coarser question the three filtering surfaces ask — "what state
   is this task in" — and it exists for the same reason. FR-017 requires the
   surfaces to name a state identically, and before this the dot-colour map and
   the word map were written out three times: in `BoardPage`, in `TaskFilterBar`
   and in `TaskRow`. Two of the three carried a comment forbidding a fourth
   wording while being the second and third, and they had already drifted — the
   same state read "3 errors", "Errors" and "Error" on three pages.

   The one distinction that survives is declared rather than accidental: `one`
   describes a single task, `many` and `counted` name a set of them. A row says
   "Error" because it is one task; a filter pill says "Errors" because it stands
   for all of them.
   --------------------------------------------------------------------------- */

/** Every state a task is summarised as. `at-rest` is the one nothing filters by. */
export type TaskState = BoardStateFilter | 'at-rest';

export interface TaskStateWords {
  /** One task in this state — what a list row shows. */
  one: string;
  /** A set of them — what a filter pill shows when it carries no count. */
  many: string;
  /** `n` of them — what a filter pill shows when it does. */
  counted: (n: number) => string;
}

export const TASK_STATE_WORDS: Record<TaskState, TaskStateWords> = {
  running: {
    one: 'In progress',
    many: 'In progress',
    counted: (n) => `${String(n)} in progress`,
  },
  waiting: {
    one: 'Waiting on you',
    many: 'Waiting on you',
    counted: (n) => `${String(n)} waiting on you`,
  },
  failed: {
    one: 'Error',
    many: 'Errors',
    counted: (n) => `${String(n)} ${n === 1 ? 'error' : 'errors'}`,
  },
  'at-rest': {
    one: 'At rest',
    many: 'At rest',
    counted: (n) => `${String(n)} at rest`,
  },
};

const READER_TASK_STATE_WORDS: Record<TaskState, TaskStateWords> = {
  ...TASK_STATE_WORDS,
  waiting: {
    one: READER_WAITING_LABEL,
    many: READER_WAITING_LABEL,
    counted: (n) => `${String(n)} waiting on a member`,
  },
};

/**
 * `TASK_STATE_WORDS` in the words that are true of the person reading them
 * (spec 193): a reader (`useCanWork()` false) is told a gate waits on a
 * member, never on them. Every surface naming a waiting task reads this, so
 * the board, the task list and Overview cannot say it two ways.
 */
export function taskStateWords(
  canWork: boolean,
): Record<TaskState, TaskStateWords> {
  return canWork ? TASK_STATE_WORDS : READER_TASK_STATE_WORDS;
}

/**
 * The dot beside a state's name, in the board's own role colours.
 *
 * Spelled out rather than built from a template, for the reason the table above
 * gives: Tailwind's scanner reads source text, so a class it never sees written
 * is a class it never emits. `at-rest` is the one state with no role of its own
 * — nothing is happening, which is not a board state — so it takes the muted
 * foreground.
 */
export const TASK_STATE_DOT: Record<TaskState, string> = {
  running: 'bg-board-state-running',
  waiting: 'bg-board-state-waiting',
  failed: 'bg-board-state-failed',
  'at-rest': 'bg-muted-foreground',
};
